/**
 * JSON テキスト中の「パス → 行番号」を求める小さな走査器。
 *
 * WHY: サーバーの検証エラーは `leaderboards.0.stat` のようなパスで返る。長い config でそれを目で探すのは辛いので、
 * エディタ上の該当行へ飛べるようにする。JSON.parse は位置情報を返さないため、値の開始位置だけを記録する
 * 最小限の再帰下降を書く（検証自体は JSON.parse とサーバーに任せ、ここは壊れた入力なら黙って諦める）。
 */

export function locatePaths(text: string): Map<string, number> {
  const out = new Map<string, number>();
  let i = 0;
  let line = 1;

  const ws = () => {
    while (i < text.length) {
      const c = text[i];
      if (c === '\n') line++;
      if (c === ' ' || c === '\n' || c === '\r' || c === '\t') i++;
      else break;
    }
  };
  const str = (): string => {
    // 呼び出し時 text[i] === '"'
    let s = '';
    i++;
    while (i < text.length && text[i] !== '"') {
      if (text[i] === '\\') {
        s += text[i + 1] ?? '';
        i += 2;
      } else {
        if (text[i] === '\n') line++;
        s += text[i];
        i++;
      }
    }
    i++;
    return s;
  };
  const value = (path: string, depth: number): void => {
    if (depth > 64) throw new Error('too deep');
    ws();
    out.set(path, line);
    const c = text[i];
    if (c === '{') {
      i++;
      ws();
      if (text[i] === '}') {
        i++;
        return;
      }
      for (;;) {
        ws();
        if (text[i] !== '"') throw new Error('key');
        const keyLine = line;
        const k = str();
        const child = path ? `${path}.${k}` : k;
        ws();
        if (text[i] !== ':') throw new Error(':');
        i++;
        value(child, depth + 1);
        // キーの行を優先（値が次の行から始まる配列・オブジェクトでも、キーの行を指す方が分かりやすい）
        out.set(child, keyLine);
        ws();
        if (text[i] === ',') {
          i++;
          continue;
        }
        if (text[i] === '}') {
          i++;
          return;
        }
        throw new Error('obj');
      }
    }
    if (c === '[') {
      i++;
      ws();
      if (text[i] === ']') {
        i++;
        return;
      }
      for (let idx = 0; ; idx++) {
        value(path ? `${path}.${idx}` : String(idx), depth + 1);
        ws();
        if (text[i] === ',') {
          i++;
          continue;
        }
        if (text[i] === ']') {
          i++;
          return;
        }
        throw new Error('arr');
      }
    }
    if (c === '"') {
      str();
      return;
    }
    // 数値・true・false・null
    while (i < text.length && !/[\s,\]}]/.test(text[i]!)) i++;
  };

  try {
    value('', 0);
  } catch {
    /* 壊れた JSON: 分かったところまで返す */
  }
  return out;
}

/** 最も深く一致する既知パスの行（`a.b.3.c` が無ければ `a.b.3`、`a.b` …と遡る） */
export function lineForPath(map: Map<string, number>, path: string): number | null {
  const parts = path.split('.').filter(Boolean);
  while (parts.length) {
    const hit = map.get(parts.join('.'));
    if (hit) return hit;
    parts.pop();
  }
  return null;
}

/** JSON.parse のエラーから行・列を出す（エンジンごとに文言が違うので position と line/column の両方に対応） */
export function parseErrorLocation(text: string, message: string): { line: number; column: number } | null {
  const lc = /line (\d+) column (\d+)/i.exec(message);
  if (lc) return { line: Number(lc[1]), column: Number(lc[2]) };
  const pos = /position (\d+)/i.exec(message);
  if (!pos) return null;
  const p = Number(pos[1]);
  const before = text.slice(0, p);
  const line = before.split('\n').length;
  return { line, column: p - before.lastIndexOf('\n') };
}
